import { Fragment, useMemo } from 'react';
import { Badge, EmptyState } from '@indipia/ui-core';
import type { ScheduleTabContent, ScheduleTask } from '../api/customerHome';
import { DOW_KO, diffDays, fmtDate, fmtMd, parseDtm } from './format';

// 공종 막대 색 — 공종마다 구분되도록 상태·브랜드 토큰을 돌려 쓴다(임의 hex 금지).
const BAR_COLORS = [
  'var(--color-info)',
  'var(--shell-point)',
  'var(--color-success)',
  'var(--indipia-purple-500)',
  'var(--color-warning)',
  'var(--color-danger)',
];

const ROW = 'var(--spacing-07)'; // 행 높이
const DAY_COL = 'var(--spacing-07)'; // 날짜 칸 폭
const NAME_COL = 'calc(var(--spacing-12) + var(--spacing-07))'; // 공종명 칸 폭(128px) — "공종명 · N일"이 말줄임 없이 읽히는 정도

interface Props {
  content: ScheduleTabContent;
}

function sortTasks(tasks: ScheduleTask[]): ScheduleTask[] {
  return tasks.slice().sort((a, b) => a.displayOrder - b.displayOrder || a.startDate.localeCompare(b.startDate));
}

/**
 * 공사스케줄 탭 — 공사기간 + 전체 일정표(간트). 반장 이름·연락처는 서버가 비워 보내며 여기서도 쓰지 않는다.
 * 공종별 "변경" 표시 — 직전 방문 이후 공유된 일정에서 날짜가 바뀐 공종(`changed`)은 공종명 옆 주황 점과
 * 간트 바 점선 외곽선으로 구분한다(화면기획서 고객대시보드 NEW 표시).
 */
export function ScheduleTab({ content }: Props) {
  const tasks = useMemo(() => sortTasks(content.tasks ?? []), [content.tasks]);
  const startIso = content.startDate;
  const endIso = content.endDate;

  if (!startIso || !endIso || tasks.length === 0) {
    return <EmptyState title="등록된 공사 일정이 없습니다" description="사장님이 공사 일정을 공유하면 여기에 표시됩니다." />;
  }

  const start = parseDtm(startIso);
  const days = diffDays(start, parseDtm(endIso)) + 1;
  const today = diffDays(start, new Date());
  const ms = content.nextMilestone ?? null;
  const msIdx = ms ? diffDays(start, parseDtm(ms.milestoneDate)) : -1;
  const msInRange = msIdx >= 0 && msIdx < days;
  // D-Day — 개별 일정 뷰어(ScheduleShareViewPage)가 고객 포함 모든 수신자에게 보이던 정보. 종료일 기준.
  const dDiff = diffDays(new Date(), parseDtm(endIso));
  const dDayLabel = dDiff > 0 ? `D-${dDiff}` : dDiff === 0 ? 'D-Day' : `D+${Math.abs(dDiff)}`;
  const dayDates = Array.from({ length: days }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });

  // 공종명 칸은 왼쪽 정렬(justify-center와 같이 두면 정렬 유틸 우선순위에 따라 가운데로 밀리므로 따로 만든다)
  const cellBase = 'relative flex items-center border-b border-border text-caption text-text-disabled';
  const cell = `${cellBase} justify-center`;
  // 가로 스크롤 시 공종명 칸이 뒤따라오는 날짜 칸·막대(relative) 위에 그려지도록 z-sticky로 올린다(z-base=0은 같은 층이라 뒤 칸이 덮는다).
  const sticky = `${cellBase} sticky left-0 z-sticky min-w-0 justify-start overflow-hidden whitespace-nowrap border-r bg-surface-raised pl-04 pr-03 font-bold text-text-secondary`;

  return (
    <div className="flex flex-col gap-05">
      <div className="flex items-center justify-between rounded-xl border border-border bg-surface-raised px-05 py-05">
        <div>
          <div className="text-caption font-bold text-text-disabled">공사기간</div>
          <div className="mt-01 text-body-sm font-bold text-text">
            {fmtDate(startIso)} ~ {fmtMd(endIso)} · 총 {days}일
          </div>
        </div>
        <Badge status="info">
          {dDayLabel}
        </Badge>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-surface-raised">
        <div className="px-05 pb-03 pt-04 text-caption font-bold text-text-secondary">전체 일정표</div>
        {/* isolate — 안쪽 z-index가 페이지 상단 고정 헤더(z-sticky)와 겹쳐 다투지 않도록 간트만 독립된 층으로 격리 */}
        <div className="isolate overflow-x-auto pb-04">
          <div
            className="grid w-max"
            style={{ gridTemplateColumns: `${NAME_COL} repeat(${days}, ${DAY_COL})`, gridAutoRows: ROW }}
          >
            {/* 날짜 머리줄 */}
            <div className={sticky}>공종</div>
            {dayDates.map((d, i) => (
              <div
                key={i}
                className={`${cell} flex-col font-bold ${i === today ? 'text-point' : d.getDay() === 0 ? 'text-danger' : d.getDay() === 6 ? 'text-info' : 'text-text-secondary'}`}
                style={{
                  background: i === today ? 'var(--shell-point-subtle)' : undefined,
                  borderLeft: msInRange && i === msIdx ? '1px dashed var(--shell-point)' : undefined,
                }}
              >
                <span>
                  {d.getMonth() + 1}/{d.getDate()}
                </span>
                <span>{DOW_KO[d.getDay()]}</span>
              </div>
            ))}

            {/* 마일스톤 줄 */}
            {msInRange && ms && (
              <>
                <div className={`${sticky}`}>마일스톤</div>
                {dayDates.map((_, i) => (
                  <div key={i} className={cell} style={{ borderLeft: i === msIdx ? '1px dashed var(--shell-point)' : undefined }}>
                    {i === msIdx && (
                      <>
                        <span aria-hidden="true" className="h-03 w-03 shrink-0 rotate-45 rounded-xs" style={{ background: 'var(--shell-point)' }} />
                        {/* 이름은 마름모 옆 같은 줄에 한 번만 — 뒤쪽 절반이면 왼쪽에 붙여 일정표 오른쪽 끝에서 잘리지 않게 한다 */}
                        <span
                          className={`absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-sm px-02 font-bold ${msIdx >= days / 2 ? 'right-1/2 mr-03' : 'left-1/2 ml-03'}`}
                          style={{ background: 'var(--color-infoSubtle)', color: 'var(--shell-point)' }}
                        >
                          {ms.milestoneName}
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* 공종 줄 */}
            {tasks.map((t, ti) => {
              const from = Math.max(0, diffDays(start, parseDtm(t.startDate)));
              const to = Math.min(days - 1, diffDays(start, parseDtm(t.endDate)));
              const span = to - from + 1;
              return (
                <Fragment key={t.taskId}>
                  <div className={sticky}>
                    {/* 긴 공종명은 말줄임 — 고정 폭을 넘쳐 옆 막대와 겹치지 않게 하되 "· N일"은 항상 보이게 한다 */}
                    <span className="min-w-0 truncate">{t.workTypeName ?? ''}</span>
                    {t.changed && (
                      <span
                        role="img"
                        aria-label="일정 변경"
                        title="일정 변경"
                        className="ml-01 h-02 w-02 shrink-0 rounded-full"
                        style={{ background: 'var(--color-warning)' }}
                      />
                    )}
                    <span className="shrink-0 whitespace-nowrap">&nbsp;· {t.duration}일</span>
                  </div>
                  {dayDates.map((_, i) => {
                    if (span > 0 && i > from && i <= to) return null; // 막대가 덮는 칸
                    const border = msInRange && i === msIdx ? { borderLeft: '1px dashed var(--shell-point)' } : undefined;
                    if (span > 0 && i === from) {
                      return (
                        <div key={i} className={cell} style={{ gridColumn: `span ${span}`, ...border }}>
                          <div
                            className="flex h-06 w-full items-center overflow-hidden whitespace-nowrap rounded-sm pl-02 text-caption font-bold text-white"
                            style={{
                              background: BAR_COLORS[ti % BAR_COLORS.length],
                              // 변경된 공종: 바 색과 겹쳐 묻히지 않도록 바깥쪽에 점선 외곽선
                              ...(t.changed ? { outline: '2px dashed var(--shell-point)', outlineOffset: '1px' } : null),
                            }}
                          >
                            {t.duration}일
                          </div>
                        </div>
                      );
                    }
                    return <div key={i} className={cell} style={border} />;
                  })}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
